import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  Box,
  Heading3,
  Display,
  FlexDirection,
  ModalBody,
  JustifyContent,
  Overflow,
  ModalClose,
  DisplayPosition,
  Divider,
  Color,
} from '@gousto-internal/citrus-react'
import { Recipe, RecipeCoreId, RecipeUUID } from '@library/menu-service'
import { Recipes } from './Recipes'
import { DeliveryDetails } from './DeliveryDetails'
import { CheckoutButton } from './CheckoutButton'
import { OrderSummary } from './OrderSummary'
import { TransactionalOrderBanner } from './TransactionalOrderBanner'
import { UndoBanner } from '../UndoBanner/UndoBanner'

type ContentProps = {
  name: string
  maxRecipesInBox: number
  numPortions: number
  basketRecipes: {
    coreId: RecipeCoreId
    recipe: Recipe
    quantity: number
  }[]
  closeModal: () => void
  addRecipe: (id: RecipeCoreId, view: string) => void
  removeRecipe: (id: RecipeCoreId, view: string) => void
  onClickRecipe: (id: RecipeUUID) => void
  onCheckout: (e: SyntheticEvent) => void
  deliverySlot: {
    date: string
    timeStart: string
    timeEnd: string
  }
  deliveryAddress: {
    line1: string
    line2: string
    line3: string
    town: string
    postcode: string
    state: string
    county: string
  }
  deliveryPricing: {
    recipeTotal: string
    discountTotal: string
    productTotal?: string
    surchargeTotal?: string
    percentageOff: string
    finalTotal: string
    deliveryTotal: string
  }
  isCheckoutDisabled: boolean
  isCheckoutPending: boolean
  isTransactionalOrder: boolean
}

export const Content = ({
  name,
  maxRecipesInBox,
  numPortions,
  basketRecipes,
  closeModal,
  addRecipe,
  removeRecipe,
  onClickRecipe,
  onCheckout,
  deliverySlot,
  deliveryAddress,
  deliveryPricing,
  isCheckoutDisabled,
  isCheckoutPending,
  isTransactionalOrder,
}: ContentProps) => {
  const [lastRemoved, setLastRemoved] = useState<Recipe | null>(null)
  const bannerTimeoutId = useRef<NodeJS.Timeout | null>(null)

  const clearLastRemoved = () => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
      bannerTimeoutId.current = null
    }
    setLastRemoved(null)
  }

  const scheduleAutoHide = () => {
    bannerTimeoutId.current = setTimeout(clearLastRemoved, 3000)
  }

  const handleRemove = (recipe: Recipe) => {
    if (bannerTimeoutId.current) {
      clearTimeout(bannerTimeoutId.current)
    }
    setLastRemoved(recipe)
    scheduleAutoHide()
    removeRecipe(recipe.coreId, 'modal_basket')
  }

  const handleUndo = () => {
    if (!lastRemoved) {
      return
    }
    addRecipe(lastRemoved.coreId, 'modal_basket')
    clearLastRemoved()
  }

  useEffect(() => () => clearLastRemoved(), [])

  const numberOfRecipesInBasket = basketRecipes.reduce((total, item) => total + item.quantity, 0)
  const isOverLimitBy = numberOfRecipesInBasket - maxRecipesInBox

  return (
    <>
      <Box paddingH={5}>
        <ModalClose onClose={closeModal} />
        <Box position={DisplayPosition.Relative} paddingH={0} paddingBottom={3}>
          <Heading3>{`${name}'s basket`}</Heading3>
        </Box>
        <Divider />
        <ModalBody paddingV={0} paddingH={0}>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            justifyContent={JustifyContent.FlexStart}
            flexGrow={1}
            minHeight={['60vh']}
            style={{ paddingTop: '1rem' }}
            overflowY={Overflow.Scroll}
          >
            {isTransactionalOrder && <TransactionalOrderBanner />}
            <Recipes
              basketRecipes={basketRecipes}
              addRecipe={addRecipe}
              removeRecipe={handleRemove}
              onClickRecipe={onClickRecipe}
              numberOfRecipesInBasket={numberOfRecipesInBasket}
              numPortions={numPortions}
            />
            <DeliveryDetails deliverySlot={deliverySlot} deliveryAddress={deliveryAddress} />
            <OrderSummary
              deliveryPricing={deliveryPricing}
              numberOfRecipes={numberOfRecipesInBasket}
              shouldShowOrderSummary={numberOfRecipesInBasket <= maxRecipesInBox}
            />
          </Box>
        </ModalBody>
      </Box>
      <Box
        display={Display.Flex}
        width="100%"
        justifyContent={JustifyContent.Center}
        style={{
          position: 'sticky',
          paddingTop: '1rem',
          paddingBottom: '1.5rem',
          boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.15)',
        }}
        bg={Color.White}
        bottom="0"
        left="0"
        right="0"
      >
        {lastRemoved && (
          <Box
            position={DisplayPosition.Absolute}
            width="400px"
            top="-50px"
            left="50%"
            style={{ transform: 'translateX(-50%)' }}
          >
            <UndoBanner recipe={lastRemoved} onUndo={handleUndo} />
          </Box>
        )}
        <CheckoutButton
          isOverLimitBy={isOverLimitBy}
          maxRecipesInBox={maxRecipesInBox}
          onCheckout={onCheckout}
          isCheckoutDisabled={!!lastRemoved || isCheckoutDisabled}
          isCheckoutPending={isCheckoutPending}
        />
      </Box>
    </>
  )
}
